import React from 'react';
import { Tabs } from 'antd';
import { useTranslation } from 'react-i18next';
import { collect } from 'collect.js';
import { useSelector } from 'react-redux';
import usePermissions, { ManagerPermission } from '../../../hooks/auth/usePermissions';
import DateRangePicker, {
  filter_all,
  filter_last_month,
  filter_last_week,
  filter_this_month,
  filter_this_week,
  filter_this_year,
  filter_today,
  filter_yesterday,
  variant_dropdown,
  variant_radio_group,
} from '../../../components/filters/DateRangePicker';
import useHelpers from '../../../hooks/context/useHelpers';
import useDateRangeFilter from '../../../hooks/utility/useDateRangeFilter';
import useScreenType from '../../../hooks/context/useScreenType';
import General from './general';
import RestaurantDetails from './restaurant-details';
import ExternalDeliveries from './external-deliveries';
import useServiceProvider from '../../../hooks/data/useServiceProvider';
import OrderConversion from '../misc/order-conversion';
import MobileApp from './mobile-app/MobileApp';
import CustomerSuccessTeam from './customer-success-team/CustomerSuccessTeam';
import OnboardingTeam from './onboarding-team/OnboardingTeam';

export const tabs = {
  general: 'general',
  restaurant_details: 'restaurant_details',
  order_conversion: 'order_conversion',
  external_delivery: 'external_delivery',
  mobile_app: 'mobile_app',
  customer_success_team: 'customer_success_team',
  onboarding_team: 'onboarding_team',
};

export default function ServiceProviderDashboard() {
  const { t } = useTranslation(undefined, { keyPrefix: 'Views:Dashboard:ServiceProvider:ServiceProviderDashboard' });

  const { can, isAdmin } = usePermissions();
  const { isDesktop } = useScreenType();
  const { getActiveTab, setActiveTab } = useHelpers();
  const { hasExternalDelivery, hasExternalAnalytics, hasPlgOnboarding } = useServiceProvider();

  const { serviceProvider } = useSelector((state) => state.currentServiceProvider);
  const [dateRangeFilter, setDateRangeFilter] = useDateRangeFilter({
    period: filter_this_month,
  });
  const isCustomerSuccessTabPublished = useSelector(
    (state) => state.currentServiceProvider.serviceProvider?.is_customer_success_metrics_published,
  );

  const visibleTabs = collect()
    .when(can(ManagerPermission.DashboardViewRestaurantDetailsTab), (collection) =>
      collection
        .merge([tabs.general, tabs.restaurant_details, tabs.mobile_app])
        .when(hasExternalAnalytics, (collection) => collection.push(tabs.order_conversion))
        // External delivery + customer-success team are not applicable to
        // self-serve (PLG) partners (decided 2026-09-04).
        .when(hasExternalDelivery && !hasPlgOnboarding, (collection) => collection.push(tabs.external_delivery)),
    )
    .when(!hasPlgOnboarding && (isCustomerSuccessTabPublished || isAdmin), (collection) =>
      collection.push(tabs.customer_success_team),
    )
    .when(serviceProvider?.is_master, (collection) => collection.push(tabs.onboarding_team))
    .toArray();

  const activeTab = getActiveTab('dashboard.section', visibleTabs[0] ?? tabs.general);
  const TabContent =
    {
      [tabs.general]: <General dateRangeFilter={dateRangeFilter} renderDateRangeFilter={renderDateRangeFilter} />,
      [tabs.restaurant_details]: (
        <RestaurantDetails dateRangeFilter={dateRangeFilter} renderDateRangeFilter={renderDateRangeFilter} />
      ),
      [tabs.order_conversion]: (
        <OrderConversion dateRangeFilter={dateRangeFilter} renderDateRangeFilter={renderDateRangeFilter} />
      ),
      [tabs.external_delivery]: (
        <ExternalDeliveries dateRangeFilter={dateRangeFilter} renderDateRangeFilter={renderDateRangeFilter} />
      ),
      [tabs.mobile_app]: <MobileApp dateRangeFilter={dateRangeFilter} renderDateRangeFilter={renderDateRangeFilter} />,
      [tabs.customer_success_team]: (
        <CustomerSuccessTeam dateRangeFilter={dateRangeFilter} renderDateRangeFilter={renderDateRangeFilter} />
      ),
      [tabs.onboarding_team]: (
        <OnboardingTeam dateRangeFilter={dateRangeFilter} renderDateRangeFilter={renderDateRangeFilter} />
      ),
    }[activeTab] ?? null;

  const onChangeDateRangeFilter = (filter) => {
    if (filter.period) {
      setDateRangeFilter({ period: filter.period });

      return;
    }

    setDateRangeFilter({
      start_date: filter.start_date,
      end_date: filter.end_date,
    });
  };

  function renderDateRangeFilter(props) {
    return (
      <div className="my-lg">
        <DateRangePicker
          variant={isDesktop ? variant_radio_group : variant_dropdown}
          excludeFilters={[
            filter_today,
            filter_yesterday,
            filter_this_week,
            filter_last_week,
            filter_this_month,
            filter_last_month,
            filter_this_year,
            filter_all,
          ]}
          initialValue={dateRangeFilter}
          onChange={onChangeDateRangeFilter}
          defaultFilter="filter_period_this_month"
          customFilters={[
            {
              id: 'filter_period_today',
              name: t('date_range.today'),
              period: 'today',
            },
            {
              id: 'filter_period_yesterday',
              name: t('date_range.yesterday'),
              period: 'yesterday',
            },
            {
              id: 'filter_period_this_week',
              name: t('date_range.this_week'),
              period: 'this_week',
            },
            {
              id: 'filter_period_this_month',
              name: t('date_range.this_month'),
              period: 'this_month',
            },
            {
              id: 'filter_period_this_year',
              name: t('date_range.this_year'),
              period: 'this_year',
            },
            {
              id: 'filter_period_last_week',
              name: t('date_range.last_week'),
              period: 'last_week',
            },
            {
              id: 'filter_period_last_month',
              name: t('date_range.last_month'),
              period: 'last_month',
            },
            {
              id: 'filter_period_last_year',
              name: t('date_range.last_year'),
              period: 'last_year',
            },
            {
              id: 'filter_period_all',
              name: t('date_range.all'),
              start_date: null,
              end_date: null,
            },
          ]}
          {...(props ?? {})}
        />
      </div>
    );
  }

  return (
    visibleTabs.length > 0 && (
      <>
        <Tabs
          activeKey={activeTab ?? visibleTabs[0] ?? tabs.general}
          onChange={(tab) => setActiveTab(tab, 'dashboard.section')}
          items={collect([
            {
              key: tabs.general,
              label: t(`tabs.${tabs.general}`),
            },
            {
              key: tabs.restaurant_details,
              label: t(`tabs.${tabs.restaurant_details}`),
            },
            {
              key: tabs.order_conversion,
              label: t(`tabs.${tabs.order_conversion}`),
            },
            {
              key: tabs.external_delivery,
              label: t(`tabs.${tabs.external_delivery}`),
            },
            {
              key: tabs.mobile_app,
              label: t(`tabs.${tabs.mobile_app}`),
            },
            {
              key: tabs.customer_success_team,
              label: t(`tabs.${tabs.customer_success_team}`),
            },
            {
              key: tabs.onboarding_team,
              label: t(`tabs.${tabs.onboarding_team}`),
            },
          ])
            .whereIn('key', visibleTabs)
            .toArray()}
        />
        {visibleTabs.includes(activeTab) ? TabContent : null}
      </>
    )
  );
}
